"use client";

import { useEffect, useRef, useState } from "react";
import { m as motion } from "framer-motion";
import { useLocale } from "next-intl";
import { Button } from "@/components/ui/button";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";

/**
 * Calendario TidyCal a TUTTA LARGHEZZA (solo desktop, xl+). Prima stava dentro
 * la colonna sinistra dell'hero e lasciava vuoto lo spazio a destra: ora e' una
 * sezione dedicata full-width. Mantiene l'id `tidycal-desktop` che e' il target
 * dello scroll dai pulsanti "Prenota una call". Mount lazy su avvicinamento o
 * quando si arriva con hash #tidycal / #calendly.
 */
export function TidyCalDesktopSection() {
  const locale = useLocale();
  const isItalian = locale.startsWith("it");
  const [show, setShow] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (hash === "#tidycal" || hash === "#calendly") {
      setShow(true);
      return;
    }
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShow(true);
          observer.disconnect();
        }
      },
      { threshold: 0.01, rootMargin: "300px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="section-padding pt-0 hidden xl:block">
      <div className="container-wide">
        <motion.div
          id="tidycal-desktop"
          ref={sentinelRef}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {show ? (
            <CalendlyEmbed eager={false} />
          ) : (
            <div className="surface-card flex flex-col items-start gap-4 p-6 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-muted-foreground">
                {isItalian
                  ? "Carica il calendario per scegliere data e ora della call."
                  : "Load the calendar to pick date and time for the call."}
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShow(true)}
              >
                {isItalian ? "Carica calendario" : "Load calendar"}
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
